// Settings da VIAGEM (compartilhados entre admin e convidados).
//
// Blob: `trips/{tripId}/settings` via cloudSync.
// Por enquanto guarda só `currencyRates` — cotações compartilhadas pra
// que todos os participantes vejam os mesmos totais convertidos.
//
// Fluxo de uso (em reloadData de cada página financeira):
//   const userSettings = await getUserSettings();
//   const tripSettings = await loadTripSettings(tripId);
//   const rates = mergeRates(userSettings, tripSettings);
//   // ...usa STATE.settings.currencyRates = rates
//   // Em background, propaga rates do user pra trip se faltarem.
//   syncRatesToTripIfNeeded(tripId, userSettings, tripSettings);
//
// Estratégia "merge":
//   - Para cada currency, prioridade: tripSettings > userSettings.
//   - Convidado sem rates próprias → vê as do admin (que estão na trip).
//   - Admin atualiza rates: o caller persiste em ambos (user e trip).

import { cloudLoad, cloudSave } from './cloudSync.js';

/** Carrega settings da viagem (pode falhar silenciosamente). */
export async function loadTripSettings(tripId) {
  if (!tripId) return {};
  try {
    const blob = await cloudLoad(tripId, 'settings');
    return blob || {};
  } catch (err) {
    console.warn('[tripSettings] load failed:', err);
    return {};
  }
}

/** Salva settings da viagem (merge). */
export async function saveTripSettings(tripId, patch) {
  if (!tripId) throw new Error('saveTripSettings: tripId obrigatório');
  const current = await loadTripSettings(tripId);
  await cloudSave(tripId, 'settings', {
    ...current, ...patch, _ts: Date.now(),
  });
}

/**
 * Mescla rates do user e da trip. Trip vence em conflito (é a fonte
 * compartilhada). User serve de fallback pras moedas que a trip não
 * tem cadastradas ainda.
 *
 * Resultado é seguro pra usar como `STATE.settings.currencyRates`.
 */
export function mergeRates(userSettings, tripSettings) {
  const userRates = userSettings?.currencyRates || {};
  const tripRates = tripSettings?.currencyRates || {};
  return { ...userRates, ...tripRates };
}

/**
 * Se o user tem rates que a trip não tem, salva-as na trip (background).
 * Idempotente: não chama Firestore se não há nada novo a propagar.
 * Falhas são logadas mas não propagam — não bloqueia o fluxo do caller.
 *
 * Retorna `true` se houve sync, `false` caso contrário.
 */
export async function syncRatesToTripIfNeeded(tripId, userSettings, tripSettings) {
  if (!tripId) return false;
  const userRates = userSettings?.currencyRates || {};
  const tripRates = tripSettings?.currencyRates || {};
  const missing = {};
  let needsSave = false;
  for (const [code, value] of Object.entries(userRates)) {
    if (tripRates[code] == null && isFinite(Number(value))) {
      missing[code] = Number(value);
      needsSave = true;
    }
  }
  if (!needsSave) return false;
  try {
    await saveTripSettings(tripId, {
      currencyRates: { ...tripRates, ...missing },
    });
    return true;
  } catch (err) {
    console.warn('[tripSettings] sync rates failed:', err);
    return false;
  }
}
